import React from "react";
import {NavLink, useLocation} from "react-router-dom";
import {css} from "@emotion/css";
import {FlexColumn, styled, theme} from "@reside/ui";
import {RoleNameEnum} from "../../../utils/types/common";

import {AdminRoles} from "../../../atoms/admin-roles";
import {RequiredUserPermissionsSome} from "../../../atoms/admin-user-permissions/RequiredUserPermissionsSome";
import {
  MenuCategory,
  MenuContainer,
  MenuGroup,
  MenuGroupTitle,
} from "../../../atoms/menu";
import {TemporaryHideChart} from "../TemporaryHideChart";
import {
  getMenuItemAccess,
  getMenuItemPermissions,
  getMenuItemSections,
  MenuItem as ReportingMenuItem,
} from "./helpers";
import {ReportingAdvancedAnalyticsGroups} from "./ReportingAdvancedAnalyticsGroups";
import {
  LOADING_PLACEHOLDER,
  MenuItemEllipsis,
  ReportingWorkflowGroups,
} from "./ReportingWorkflowGroup";
import {ChecklistItem} from "../../../services/ReportingService";
import {QuicksightDashboard} from "../../../services/QuicksightService";
import {menuSectionPath} from "../reporting-paths";
import {useReportContext} from "../ReportContext";
import {useReportingMenuDefinition} from "./useReportingMenuDefinition";

export {LOADING_PLACEHOLDER};

type Props = Readonly<{
  checklistItems?: ReadonlyArray<ChecklistItem>;
  dashboardItems?: ReadonlyArray<QuicksightDashboard>;
}>;

export const ReportingMenu = ({
  checklistItems = [],
  dashboardItems = [],
}: Props) => {
  const {baseDateRange} = useReportContext();
  const location = useLocation();
  const menu = useReportingMenuDefinition(checklistItems, dashboardItems);

  const renderGate = (
    menuItem: ReportingMenuItem,
    children: React.ReactNode,
  ) => (
    <MenuAccessGate key={menuItem.urlComponent} menuItem={menuItem}>
      {children}
    </MenuAccessGate>
  );

  return (
    <ReportingMenuColumn>
      {menu.map(category => {
        const {children: subCategories = []} = category;
        const isReporting = category.urlComponent === "reporting";
        const isAnalytics = category.urlComponent === "analytics";

        return (
          <MenuAccessGate key={category.urlComponent} menuItem={category}>
            <ReportingMenuContainer>
              <MenuCategory
                active={location.pathname.includes(`/${category.urlComponent}`)}
                iconName={category.icon}
                title={category.label}
              />
              {subCategories.length > 0 && (
                <CategoryItems>
                  {isReporting ? (
                    <MenuGroup>
                      <ReportingWorkflowGroups
                        workflows={subCategories}
                        checklistItems={checklistItems}
                        categoryUrl={category.urlComponent}
                        baseDateRange={baseDateRange}
                        pathname={location.pathname}
                        renderGate={renderGate}
                      />
                    </MenuGroup>
                  ) : isAnalytics ? (
                    <MenuGroup>
                      <ReportingAdvancedAnalyticsGroups
                        subCategories={subCategories}
                        categoryUrl={category.urlComponent}
                        pathname={location.pathname}
                        renderGate={renderGate}
                      />
                    </MenuGroup>
                  ) : (
                    subCategories.map(subCategory => (
                      <MenuAccessGate
                        key={subCategory.urlComponent}
                        menuItem={subCategory}
                      >
                        <MenuGroup>
                          <MenuGroupTitle>{subCategory.label}</MenuGroupTitle>
                          {(subCategory.children ?? []).map(section => (
                            <MenuAccessGate
                              key={section.urlComponent}
                              menuItem={section}
                            >
                              <MenuItemEllipsis>
                                {section.urlComponent ===
                                LOADING_PLACEHOLDER ? (
                                  section.label
                                ) : (
                                  <NavLink
                                    className={({isActive}) =>
                                      css`
                                        color: inherit;
                                        text-decoration: none;
                                        ${isActive &&
                                        `color: ${theme.color.primary};`}
                                      `
                                    }
                                    to={menuSectionPath(
                                      category.urlComponent,
                                      subCategory.urlComponent,
                                      section.urlComponent,
                                    )}
                                    title={section.label}
                                  >
                                    {section.label}
                                  </NavLink>
                                )}
                              </MenuItemEllipsis>
                            </MenuAccessGate>
                          ))}
                        </MenuGroup>
                      </MenuAccessGate>
                    ))
                  )}
                </CategoryItems>
              )}
            </ReportingMenuContainer>
          </MenuAccessGate>
        );
      })}
      <AdminRoles
        roles={[RoleNameEnum.RESIDE_ADMIN, RoleNameEnum.ORGANIZATION_ADMIN]}
      >
        <ReportingMenuContainer>
          <PermissionsMenuSection />
        </ReportingMenuContainer>
      </AdminRoles>
    </ReportingMenuColumn>
  );
};

const MenuAccessGate = ({
  menuItem,
  children,
}: {
  menuItem: ReportingMenuItem;
  children: React.ReactNode;
}) => {
  const permissions = getMenuItemPermissions(menuItem);
  const access = getMenuItemAccess(menuItem, permissions);

  if (access === "hide") {
    return null;
  }

  if (access === "allow") {
    return <>{children}</>;
  }

  return (
    <RequiredUserPermissionsSome permissions={permissions}>
      <TemporaryHideChart chartIds={getMenuItemSections(menuItem)}>
        {children}
      </TemporaryHideChart>
    </RequiredUserPermissionsSome>
  );
};

const PermissionsMenuSection = () => {
  const location = useLocation();
  const isCategoryActive = location.pathname.startsWith("/admin/permissions");
  const isManagementActive = location.pathname.startsWith(
    "/admin/permissions/management",
  );

  return (
    <>
      <MenuCategory
        active={isCategoryActive}
        iconName="permissions"
        title="Permissions"
      />
      <CategoryItems>
        <MenuGroup>
          <MenuGroupTitle>General</MenuGroupTitle>
          <MenuItemEllipsis active={isManagementActive}>
            <NavLink
              className={({isActive}) =>
                css`
                  color: inherit;
                  text-decoration: none;
                  ${isActive && `color: ${theme.color.primary};`}
                `
              }
              to="/admin/permissions/management"
              title="Permissions Management"
            >
              Permissions Management
            </NavLink>
          </MenuItemEllipsis>
        </MenuGroup>
      </CategoryItems>
    </>
  );
};

const ReportingMenuColumn = styled(FlexColumn)`
  flex-grow: 1;
  flex-shrink: 0;
  margin-right: 20px;
  max-width: 320px;
  overflow: auto;
`;

const CategoryItems = styled.div`
  margin-top: 24px;
  margin-left: 32px;
`;

const ReportingMenuContainer = styled(MenuContainer)`
  width: 100%;
  border-radius: 8px 0 0 8px;
`;
